/**
 * Panel «Vídeo» en la ficha de especie del Portal (APP-21).
 * Dos formatos: 16:9 (web) y 9:16 (app). Subida SAS directa a species-videos.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Film, Loader2, Save, Trash2, Upload } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api-client';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';

export type SpeciesVideoAspect = '16x9' | '9x16';

export interface SpeciesVideoRow {
    id: string;
    species_id: number;
    locale: string;
    aspect: SpeciesVideoAspect;
    video_url: string;
    video_url_mobile: string | null;
    poster_url: string;
    duration_seconds: number;
    width: number | null;
    height: number | null;
    title: string;
    description: string;
    transcript: string | null;
    ai_generated: boolean;
    ai_disclosure_visible: boolean;
    ai_disclosure_text: string;
    youtube_url: string | null;
    sources: string | null;
    status: 'draft' | 'published';
    published_at: string | null;
}

const DEFAULT_DISCLOSURE =
    'Imágenes generadas con IA a partir de referencias reales de la especie, con revisión científica.';

interface AspectDraft {
    id: string | null;
    videoUrl: string;
    videoUrlMobile: string;
    posterUrl: string;
    durationSeconds: number;
    width: number;
    height: number;
    title: string;
    description: string;
    transcript: string;
    aiGenerated: boolean;
    aiDisclosureVisible: boolean;
    aiDisclosureText: string;
    youtubeUrl: string;
    sources: string;
    status: 'draft' | 'published';
}

function emptyDraft(titleHint: string): AspectDraft {
    return {
        id: null,
        videoUrl: '',
        videoUrlMobile: '',
        posterUrl: '',
        durationSeconds: 0,
        width: 0,
        height: 0,
        title: titleHint,
        description: '',
        transcript: '',
        aiGenerated: true,
        aiDisclosureVisible: false,
        aiDisclosureText: DEFAULT_DISCLOSURE,
        youtubeUrl: '',
        sources: '',
        status: 'draft',
    };
}

function rowToDraft(row: SpeciesVideoRow): AspectDraft {
    return {
        id: row.id,
        videoUrl: row.video_url || '',
        videoUrlMobile: row.video_url_mobile || '',
        posterUrl: row.poster_url || '',
        durationSeconds: row.duration_seconds || 0,
        width: row.width || 0,
        height: row.height || 0,
        title: row.title || '',
        description: row.description || '',
        transcript: row.transcript || '',
        aiGenerated: row.ai_generated !== false,
        aiDisclosureVisible: !!row.ai_disclosure_visible,
        aiDisclosureText: row.ai_disclosure_text || DEFAULT_DISCLOSURE,
        youtubeUrl: row.youtube_url || '',
        sources: row.sources || '',
        status: row.status === 'published' ? 'published' : 'draft',
    };
}

function slugifyScientific(name: string): string {
    return name
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

async function readVideoMeta(file: File): Promise<{ duration: number; width: number; height: number }> {
    const objectUrl = URL.createObjectURL(file);
    try {
        const meta = await new Promise<{ duration: number; width: number; height: number }>((resolve, reject) => {
            const el = document.createElement('video');
            el.preload = 'metadata';
            el.muted = true;
            el.playsInline = true;
            el.onloadedmetadata = () => {
                resolve({
                    duration: Math.round(el.duration || 0),
                    width: el.videoWidth || 0,
                    height: el.videoHeight || 0,
                });
            };
            el.onerror = () => reject(new Error('No se pudieron leer metadatos del vídeo'));
            el.src = objectUrl;
        });
        return meta;
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
}

function AspectVideoEditor({
    aspect,
    label,
    draft,
    onChange,
    speciesSlug,
    speciesId,
    onSaved,
    onDeleted,
}: {
    aspect: SpeciesVideoAspect;
    label: string;
    draft: AspectDraft;
    onChange: (next: AspectDraft) => void;
    speciesSlug: string;
    speciesId: number;
    onSaved: () => void;
    onDeleted: () => void;
}) {
    const hiddenVideoRef = useRef<HTMLVideoElement>(null);
    const [uploadPct, setUploadPct] = useState<number | null>(null);
    const [uploadingField, setUploadingField] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const setField = <K extends keyof AspectDraft>(key: K, value: AspectDraft[K]) => {
        onChange({ ...draft, [key]: value });
    };

    const uploadBlob = async (file: File, kind: 'video' | 'video_mobile' | 'poster') => {
        setError(null);
        setUploadingField(kind);
        setUploadPct(0);
        const ts = Date.now();
        const ext = kind === 'poster' ? 'jpg' : 'mp4';
        const suffix = kind === 'video_mobile' ? `${aspect}-mobile` : aspect;
        const path = `${speciesSlug}/${suffix}-${ts}.${ext}`;
        const contentType = kind === 'poster' ? (file.type || 'image/jpeg') : 'video/mp4';

        try {
            let next: AspectDraft = { ...draft };
            if (kind === 'video') {
                const meta = await readVideoMeta(file);
                next = {
                    ...next,
                    durationSeconds: meta.duration || next.durationSeconds,
                    width: meta.width || next.width,
                    height: meta.height || next.height,
                };
            }

            const result = await apiClient.uploadViaSas('species-videos', path, file, {
                contentType,
                cacheControl: 'public, max-age=31536000, immutable',
                onProgress: setUploadPct,
            });
            if (result.error || !result.data?.url) {
                throw new Error(result.error?.message || 'Error al subir');
            }
            const url = result.data.publicUrl || result.data.url;
            if (kind === 'video') next = { ...next, videoUrl: url };
            else if (kind === 'video_mobile') next = { ...next, videoUrlMobile: url };
            else next = { ...next, posterUrl: url };
            onChange(next);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Error al subir archivo');
        } finally {
            setUploadingField(null);
            setUploadPct(null);
        }
    };

    const handleSave = async () => {
        setError(null);
        if (!draft.videoUrl.trim() || !draft.posterUrl.trim()) {
            setError('Hacen falta el MP4 principal y el póster');
            return;
        }
        if (!draft.title.trim() || !draft.description.trim()) {
            setError('Título y descripción son obligatorios');
            return;
        }
        setSaving(true);
        try {
            const { data, error: rpcError } = await dbClient.rpc('admin_upsert_species_video', {
                p_id: draft.id,
                p_species_id: speciesId,
                p_locale: 'es',
                p_aspect: aspect,
                p_video_url: draft.videoUrl.trim(),
                p_video_url_mobile: aspect === '16x9' ? (draft.videoUrlMobile.trim() || null) : null,
                p_poster_url: draft.posterUrl.trim(),
                p_duration_seconds: Number(draft.durationSeconds) || 0,
                p_width: draft.width || null,
                p_height: draft.height || null,
                p_title: draft.title.trim(),
                p_description: draft.description.trim(),
                p_transcript: draft.transcript.trim() || null,
                p_ai_generated: draft.aiGenerated,
                p_ai_disclosure_visible: draft.aiDisclosureVisible,
                p_ai_disclosure_text: draft.aiDisclosureText.trim() || DEFAULT_DISCLOSURE,
                p_youtube_url: draft.youtubeUrl.trim() || null,
                p_sources: draft.sources.trim() || null,
                p_status: draft.status,
            });
            if (rpcError) throw new Error(rpcError.message);
            const id = Array.isArray(data) ? data[0] : data;
            if (typeof id === 'string') setField('id', id);
            onSaved();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!draft.id) {
            onChange(emptyDraft(draft.title));
            return;
        }
        if (!window.confirm(`¿Eliminar el vídeo ${label}?`)) return;
        setDeleting(true);
        setError(null);
        try {
            const { error: rpcError } = await dbClient.rpc('admin_delete_species_video', { p_id: draft.id });
            if (rpcError) throw new Error(rpcError.message);
            onDeleted();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Error al eliminar');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="rounded-xl border border-border bg-card/50 p-5 space-y-4">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <h3 className="text-base font-semibold flex items-center gap-2">
                    <Film size={16} />
                    {label}
                </h3>
                <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        draft.status === 'published'
                            ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                            : 'bg-muted text-muted-foreground'
                    }`}
                >
                    {draft.status === 'published' ? 'Publicado' : 'Borrador'}
                </span>
            </div>

            {error && (
                <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-sm space-y-1">
                    <span className="font-medium">MP4 principal *</span>
                    <input
                        type="file"
                        accept="video/mp4,.mp4"
                        className="block w-full text-sm"
                        disabled={!!uploadingField}
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) void uploadBlob(f, 'video');
                            e.target.value = '';
                        }}
                    />
                    {draft.videoUrl && (
                        <a href={draft.videoUrl} target="_blank" rel="noopener" className="text-xs text-primary break-all">
                            {draft.videoUrl}
                        </a>
                    )}
                </label>

                {aspect === '16x9' && (
                    <label className="block text-sm space-y-1">
                        <span className="font-medium">MP4 móvil 720p (opcional)</span>
                        <input
                            type="file"
                            accept="video/mp4,.mp4"
                            className="block w-full text-sm"
                            disabled={!!uploadingField}
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) void uploadBlob(f, 'video_mobile');
                                e.target.value = '';
                            }}
                        />
                        {draft.videoUrlMobile && (
                            <a href={draft.videoUrlMobile} target="_blank" rel="noopener" className="text-xs text-primary break-all">
                                {draft.videoUrlMobile}
                            </a>
                        )}
                    </label>
                )}

                <label className="block text-sm space-y-1 sm:col-span-2">
                    <span className="font-medium">Póster JPG *</span>
                    <input
                        type="file"
                        accept="image/jpeg,image/jpg,.jpg,.jpeg"
                        className="block w-full text-sm"
                        disabled={!!uploadingField}
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) void uploadBlob(f, 'poster');
                            e.target.value = '';
                        }}
                    />
                    {draft.posterUrl && (
                        <img src={draft.posterUrl} alt="" className="mt-2 max-h-40 rounded-lg border border-border object-contain" />
                    )}
                </label>
            </div>

            {uploadingField && (
                <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Upload size={14} />
                        Subiendo {uploadingField}… {uploadPct ?? 0}%
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                        <div
                            className="h-full bg-primary transition-all"
                            style={{ width: `${uploadPct ?? 0}%` }}
                        />
                    </div>
                </div>
            )}

            <div className="grid gap-3 sm:grid-cols-3">
                <label className="text-sm space-y-1">
                    <span className="font-medium">Duración (s)</span>
                    <input
                        type="number"
                        min={0}
                        value={draft.durationSeconds}
                        onChange={(e) => setField('durationSeconds', Number(e.target.value) || 0)}
                        className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                    />
                </label>
                <label className="text-sm space-y-1">
                    <span className="font-medium">Ancho</span>
                    <input
                        type="number"
                        min={0}
                        value={draft.width}
                        onChange={(e) => setField('width', Number(e.target.value) || 0)}
                        className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                    />
                </label>
                <label className="text-sm space-y-1">
                    <span className="font-medium">Alto</span>
                    <input
                        type="number"
                        min={0}
                        value={draft.height}
                        onChange={(e) => setField('height', Number(e.target.value) || 0)}
                        className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                    />
                </label>
            </div>

            <label className="block text-sm space-y-1">
                <span className="font-medium">Título *</span>
                <input
                    type="text"
                    value={draft.title}
                    onChange={(e) => setField('title', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                />
            </label>

            <label className="block text-sm space-y-1">
                <span className="font-medium">Descripción *</span>
                <textarea
                    value={draft.description}
                    onChange={(e) => setField('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                />
            </label>

            <label className="block text-sm space-y-1">
                <span className="font-medium">Transcripción</span>
                <textarea
                    value={draft.transcript}
                    onChange={(e) => setField('transcript', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm font-mono"
                />
            </label>

            <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-sm space-y-1">
                    <span className="font-medium">URL de YouTube</span>
                    <input
                        type="url"
                        value={draft.youtubeUrl}
                        onChange={(e) => setField('youtubeUrl', e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                    />
                </label>
                <label className="block text-sm space-y-1">
                    <span className="font-medium">Fuentes</span>
                    <input
                        type="text"
                        value={draft.sources}
                        onChange={(e) => setField('sources', e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                    />
                </label>
            </div>

            <div className="space-y-3 rounded-lg border border-border/60 p-3">
                <label className="flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={draft.aiGenerated}
                        onChange={(e) => setField('aiGenerated', e.target.checked)}
                    />
                    Generado con IA
                </label>
                <label className="flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={draft.aiDisclosureVisible}
                        onChange={(e) => setField('aiDisclosureVisible', e.target.checked)}
                    />
                    Mostrar aviso de IA junto al vídeo
                </label>
                <label className="block text-sm space-y-1">
                    <span className="font-medium">Texto del aviso</span>
                    <textarea
                        value={draft.aiDisclosureText}
                        onChange={(e) => setField('aiDisclosureText', e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                    />
                </label>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-sm">
                    <span className="font-medium">Estado</span>
                    <select
                        value={draft.status}
                        onChange={(e) => setField('status', e.target.value as 'draft' | 'published')}
                        className="px-3 py-2 rounded-lg border border-border bg-card text-sm"
                    >
                        <option value="draft">Borrador</option>
                        <option value="published">Publicado</option>
                    </select>
                </label>
                <button
                    type="button"
                    onClick={() => void handleSave()}
                    disabled={saving || !!uploadingField}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Guardar {label}
                </button>
                <button
                    type="button"
                    onClick={() => void handleDelete()}
                    disabled={deleting || (!draft.id && !draft.videoUrl)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
                >
                    {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    Eliminar
                </button>
            </div>

            {draft.videoUrl && (
                <div className="space-y-2">
                    <p className="text-sm font-medium">Vista previa</p>
                    <video
                        ref={hiddenVideoRef}
                        controls
                        playsInline
                        preload="metadata"
                        poster={draft.posterUrl || undefined}
                        src={draft.videoUrl}
                        className="w-full max-w-xl rounded-lg border border-border bg-black"
                        style={{ aspectRatio: aspect === '9x16' ? '9/16' : '16/9' }}
                    />
                </div>
            )}
        </div>
    );
}

export function SpeciesVideosPanel({
    speciesId,
    scientificName,
    commonNameHint,
}: {
    speciesId: number;
    scientificName: string;
    commonNameHint?: string;
}) {
    const queryClient = useQueryClient();
    const speciesSlug = slugifyScientific(scientificName) || `species-${speciesId}`;
    const titleHint = commonNameHint?.trim() || scientificName;

    const { data: rows = [], isLoading, refetch } = useQuery({
        queryKey: ['admin', 'species', 'videos', speciesId],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_list_species_videos', {
                p_species_id: speciesId,
            });
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data) as SpeciesVideoRow[];
        },
        enabled: speciesId > 0,
        staleTime: 15_000,
    });

    const [draft16, setDraft16] = useState<AspectDraft>(() => emptyDraft(titleHint));
    const [draft916, setDraft916] = useState<AspectDraft>(() => emptyDraft(titleHint));

    useEffect(() => {
        const r16 = rows.find((r) => r.aspect === '16x9');
        const r916 = rows.find((r) => r.aspect === '9x16');
        setDraft16(r16 ? rowToDraft(r16) : emptyDraft(titleHint));
        setDraft916(r916 ? rowToDraft(r916) : emptyDraft(titleHint));
    }, [rows, titleHint]);

    const refresh = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: ['admin', 'species', 'videos', speciesId] });
        void refetch();
    }, [queryClient, refetch, speciesId]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[160px]">
                <Loader2 className="animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="space-y-6 max-w-4xl">
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">
                La web pública se actualiza al ejecutar la pipeline Aipeces-Landing-Pro.
            </div>

            <AspectVideoEditor
                aspect="16x9"
                label="16:9 (web)"
                draft={draft16}
                onChange={setDraft16}
                speciesSlug={speciesSlug}
                speciesId={speciesId}
                onSaved={refresh}
                onDeleted={refresh}
            />

            <AspectVideoEditor
                aspect="9x16"
                label="9:16 (app)"
                draft={draft916}
                onChange={setDraft916}
                speciesSlug={speciesSlug}
                speciesId={speciesId}
                onSaved={refresh}
                onDeleted={refresh}
            />
        </div>
    );
}
